// /apps/patient/src/pages/billing/InvoicesList.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { Invoice } from '@prism-ehr/shared';
import { formatDate } from '@prism-ehr/shared';
import { 
  CreditCard, FileText, Clock, CheckCircle, 
  AlertCircle, ChevronRight, Filter, Package,
  Calendar, Plus, Loader2
} from 'lucide-react';

export function InvoicesList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'invoices' | 'packages' | 'subscriptions' | 'payment-methods'>('invoices');
  
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Package state
  const [packages, setPackages] = useState<any[]>([]);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  
  // Subscription state
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [activeTab, filter, page]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'invoices':
          await fetchInvoices();
          break;
        case 'packages':
          await fetchPackages();
          break;
        case 'subscriptions':
          await fetchSubscriptions();
          break;
        case 'payment-methods':
          // Redirect to existing payment methods page
          navigate('/billing/payment-methods');
          break;
      }
    } catch (error) {
      console.error(`Error loading ${activeTab}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    const params: any = { page, perPage: 20 };
    
    if (filter === 'unpaid') {
      params.status = 'sent,overdue,partial,';
    } else if (filter === 'paid') {
      params.status = 'paid';
    }

    const response = await patientApiService.getMyInvoices(params);
    setInvoices(response.items || []);
    setTotalPages(response.pageTotal || 1);
  };

  const fetchPackages = async () => {
    // Fetch patient's purchased packages /
    const myPackages = await patientApiService.getMyPackages();
    setPackages(myPackages);
    
    // Fetch available packages for purchase
    const available = await patientApiService.getAvailablePackages();
    setAvailablePackages(available);
  };

  const fetchSubscriptions = async () => {
    // Fetch active subscriptions
    const mySubscriptions = await patientApiService.getMySubscriptions();
    setSubscriptions(mySubscriptions);
    
    // Fetch available subscription plans
    const plans = await patientApiService.getAvailableSubscriptionPlans();
    setAvailablePlans(plans);
  };

  const handlePurchasePackage = async (packageId: string) => {
    try {
      const packageData = availablePackages.find(p => p.id === packageId);
      
      // Navigate to payment page with package details
      navigate('/billing/payment', {
        state: {
          type: 'package',
          packageId,
          packageData,
          amount: packageData.price
        }
      });
    } catch (error) {
      console.error('Error initiating package purchase:', error);
    }
  };
  
  const handleSubscribe = async (planId: string) => {
    try {
      const planData = availablePlans.find((p: any) => p.id === planId);

      navigate('/billing/payment', {
        state: {
          type: 'subscription',
          planId,
          planData,
          amount: planData.price
        }
      });
    } catch (error) {
      console.error('Error initiating subscription:', error);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? It will remain active until the end of your current billing period.')) return;
    try {
      await patientApiService.cancelSubscription(subscriptionId);
      await fetchSubscriptions();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusIcon = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'overdue':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'cancelled':
      case 'refunded':
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
      case 'refunded':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader 
          title="Billing & Payment"
        />

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-1" />
              Invoices
            </button>
            <button
              onClick={() => setActiveTab('packages')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'packages'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package className="inline h-4 w-4 mr-1" />
              Packages
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscriptions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="inline h-4 w-4 mr-1" />
              Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('payment-methods')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'payment-methods'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CreditCard className="inline h-4 w-4 mr-1" />
              Payment Methods
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Invoices Tab Content */}
            {activeTab === 'invoices' && (
              <>
                {/* Summary Cards */}
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Outstanding
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {formatCurrency(
                          invoices
                            .filter(i => ['sent', 'overdue', 'partial', ''].includes(i.status))
                            .reduce((sum, i) => sum + i.balance_due, 0)
                        )}
                      </dd>
                    </div>
                  </div>
                  
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Unpaid Invoices
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {invoices.filter(i => ['sent', 'overdue', 'partial', ''].includes(i.status)).length}
                      </dd>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        This Month
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {formatCurrency(
                          invoices
                            .filter(i => {
                              const invoiceDate = new Date(i.created_at);
                              const thisMonth = new Date();
                              return invoiceDate.getMonth() === thisMonth.getMonth() &&
                                     invoiceDate.getFullYear() === thisMonth.getFullYear();
                            })
                            .reduce((sum, i) => sum + i.total_amount, 0)
                        )}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="mt-8 border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    {(['all', 'unpaid', 'paid'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => {
                          setFilter(tab);
                          setPage(1);
                        }}
                        className={`
                          py-2 px-1 border-b-2 font-medium text-sm capitalize
                          ${filter === tab 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                        `}
                      >
                        {tab === 'all' ? 'All Invoices' : tab}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Invoice List */}
                <div className="mt-8">
                  {invoices.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {filter === 'unpaid' ? 'You have no unpaid invoices.' : 'You have no invoices yet.'}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {invoices.map((invoice) => (
                          <li key={invoice.id}>
                            <button
                              onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                              className="w-full px-4 py-4 sm:px-6 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  {getStatusIcon(invoice.status)}
                                  <div className="ml-4 text-left">
                                    <div className="text-sm font-medium text-gray-900">
                                      Invoice #{invoice.invoice_number}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {formatDate(invoice.service_date || invoice.created_at, 'short')}
                                      {invoice.provider_name && ` • ${invoice.provider_name}`}
                                    </div>
                                    <div className="mt-1">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                                        {invoice.status}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center">
                                  <div className="mr-4 text-right">
                                    <div className="text-sm font-medium text-gray-900">
                                      {formatCurrency(invoice.total_amount)}
                                    </div>
                                    {invoice.balance_due > 0 && invoice.status !== 'paid' && (
                                      <div className="text-sm text-red-600">
                                        Due: {formatCurrency(invoice.balance_due)}
                                      </div>
                                    )}
                                  </div>
                                  <ChevronRight className="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Packages Tab Content */}
            {activeTab === 'packages' && (
              <div className="mt-8 space-y-8">
                {/* My Packages */}
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">My Packages</h2>
                  {packages.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg shadow">
                      <Package className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No active packages</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Purchase a package below to save on future visits.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {packages.map((pkg) => (
                        <div key={pkg.id} className="bg-white shadow rounded-lg p-6">
                          <h3 className="text-lg font-medium text-gray-900">{pkg.package_name}</h3>
                          <div className="mt-2 text-3xl font-bold text-primary">
                            {pkg.credits_remaining}/{pkg.total_credits}
                          </div>
                          <p className="text-sm text-gray-500">credits remaining</p>
                          
                          {!pkg.never_expires && (
                            <div className="mt-4 text-sm">
                              <span className="text-gray-500">Expires:</span>
                              <span className="ml-1 font-medium">
                                {formatDate(pkg.expires_date, 'short')}
                              </span>
                            </div>
                          )}
                          
                          <div className="mt-4 text-sm text-gray-500">
                            Purchased: {formatDate(pkg.purchased_date, 'short')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Packages */}
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Available Packages</h2>
                  {availablePackages.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">No packages available for purchase.</p>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {availablePackages.map((pkg) => (
                        <div key={pkg.id} className="bg-white shadow rounded-lg p-6">
                          <h3 className="text-lg font-medium text-gray-900">{pkg.name}</h3>
                          {pkg.description && (
                            <p className="mt-2 text-sm text-gray-500">{pkg.description}</p>
                          )}
                          
                          <div className="mt-4">
                            <span className="text-3xl font-bold text-gray-900">
                              ${pkg.price}
                            </span>
                            <span className="text-gray-500 text-sm ml-1">
                              for {pkg.total_credits} visits
                            </span>
                          </div>
                          
                          <div className="mt-2 text-sm text-gray-500">
                            ${(pkg.price / pkg.total_credits).toFixed(2)} per visit
                          </div>
                          
                          <button
                            onClick={() => handlePurchasePackage(pkg.id)}
                            className="mt-6 w-full bg-primary text-white rounded-md py-2 px-4 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          >
                            Purchase Package
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Subscriptions Tab */}
  
            {activeTab === 'subscriptions' && (
              <div className="mt-8 space-y-8">
                {/* Active Subscriptions */}
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Active Subscriptions</h2>
                  {subscriptions.length === 0 ? (
                    <div className="text-center py-8 bg-white rounded-lg shadow">
                      <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No active subscriptions</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Subscribe to a plan below for regular savings.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {subscriptions.map((sub) => (
                        <div key={sub.id} className="bg-white shadow rounded-lg p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">{sub.plan_name}</h3>
                              <p className="text-sm text-gray-500">{sub.plan_description}</p>
                              
                              {sub.plan_type === 'visit_based' && (
                                <div className="mt-2">
                                  <span className="text-2xl font-bold text-primary">
                                    {sub.credits_remaining}/{sub.credits_per_period}
                                  </span>
                                  <span className="text-sm text-gray-500 ml-2">
                                    visits remaining this period
                                  </span>
                                </div>
                              )}
                              
                              <div className="mt-4 text-sm text-gray-500">
                                Next billing: {formatDate(sub.next_billing_date, 'short')}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-900">
                                ${sub.price}/{sub.billing_period}
                              </div>
                              {(sub.status === 'active' || sub.status === 'trialing') && (
                                <button
                                  onClick={() => handleCancelSubscription(sub.id)}
                                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                                >
                                  Cancel
                                </button>
                              )}
                              {sub.status === 'canceling' && (
                                <span className="mt-2 text-sm text-yellow-600">Canceling at period end</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Plans */}
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Available Plans</h2>
                  {availablePlans.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">No subscription plans available.</p>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {availablePlans.map((plan) => (
                        <div key={plan.id} className="bg-white shadow rounded-lg p-6">
                          <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                          {plan.description && (
                            <p className="mt-2 text-sm text-gray-500">{plan.description}</p>
                          )}
                          
                          <div className="mt-4">
                            <span className="text-3xl font-bold text-gray-900">
                              ${plan.price}
                            </span>
                            <span className="text-gray-500 text-sm ml-1">
                              /{plan.billing_period}
                            </span>
                          </div>
                          
                          {plan.plan_type === 'visit_based' && (
                            <div className="mt-2 text-sm text-gray-500">
                              {plan.credits_per_period} visits per {plan.billing_period}
                            </div>
                          )}
                          
                          {plan.trial_days > 0 && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {plan.trial_days} day free trial
                              </span>
                            </div>
                          )}
                          
                          <button
                            onClick={() => handleSubscribe(plan.id)}
                            className="mt-6 w-full bg-primary text-white rounded-md py-2 px-4 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          >
                            Subscribe
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
          </>
        )}
      </div>
    </div>
  );
}